import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { OverlayContainer } from '@angular/cdk/overlay';

import { AppRoutingModule } from 'routes/app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { AppMaterialModule } from 'modules/app.material.module';
import { FlexLayoutModule } from '@angular/flex-layout';
import { NgxWebstorageModule } from 'ngx-webstorage';
import { FileUploadModule } from 'modules/fileupload/fileupload.module';

import { AuthenticatedGuard, UnsavedChangesGuard, UnsavedChangesService } from 'routes/routes.guard';

// Components
import { ActionNeededComponent } from 'app/actionneeded/actionneeded.component';
import { AppComponent } from './app.component';
import { BoardAssistantsComponent } from 'app/boardassistants/boardassistants.component';
import { BoardRequestsComponent } from 'app/boardrequests/boardrequests.component';
import { CabinetMembersComponent } from 'app/cabinetmembers/cabinetmembers.component';
import { CabinetUnassignedComponent } from 'app/cabinetunassigned/cabinetunassigned.component';
import { CategoriesComponent } from 'app/categories/categories.component';
import { CreateComponent } from 'app/create/create.component';
import { DashboardComponent } from 'app/dashboard/dashboard.component';
import { EditRequestComponent } from 'app/editrequest/editrequest.component';
import { EmailPreferencesComponent } from 'app/emailpreferences/emailpreferences.component';
import { FacilitatorsComponent } from 'app/facilitators/facilitators.component';
import { HandleRequestComponent } from 'app/handlerequest/handlerequest.component';
import { HomeComponent } from 'app/home/home.component';
import { LoginComponent } from 'app/login/login.component';
import { OfflineComponent } from 'app/offline/offline.component';
import { OutOfOfficeComponent } from 'app/outofoffice/outofoffice.component';
import { OtherRequestsComponent } from 'app/otherrequests/otherrequests.component';
import { ReferralsComponent } from 'app/referrals/referrals.component';
import { RequestsComponent } from 'app/requests/requests.component';
import { SearchComponent } from 'app/search/search.component';
import { ViewRequestComponent } from 'app/viewrequest/viewrequest.component';

// Dialog components
import { CheckboxDialogComponent } from 'dialogs/checkbox/checkbox.dialogcomponent';
import { ConfirmationDialogComponent } from 'dialogs/confirmation/confirmation.dialogcomponent';
import { CountdownDialogComponent } from 'dialogs/countdown/countdown.dialogcomponent';
import { InputBoxDialogComponent } from 'dialogs/inputbox/inputbox.dialogcomponent';
import { LoginBoxDialogComponent } from 'dialogs/loginbox/loginbox.dialogcomponent';
import { MessageBoxDialogComponent } from 'dialogs/messagebox/messagebox.dialogcomponent';
import { NotificationDialogComponent } from 'dialogs/notification/notification.dialogcomponent';
import { OutOfOfficeDesigneeSearchDialogComponent } from 'dialogs/ooodesigneesearch/ooodesigneesearch.dialogcomponent';
import { OptionBoxDialogComponent } from 'dialogs/optionbox/optionbox.dialogcomponent';
import { ReportOptionsDialogComponent } from 'dialogs/reportoptions/reportoptions.dialogcomponent';
import { SelectBoxDialogComponent } from 'dialogs/selectbox/selectbox.dialogcomponent';
import { SelectBoxNoteDialogComponent } from 'dialogs/selectboxnote/selectboxnote.dialogcomponent';
import { SubordinateDialogComponent } from 'dialogs/subordinate/subordinate.dialogcomponent';
import { TextNoteDialogComponent } from 'dialogs/textnote/textnote.dialogcomponent';
import { TimeApproveDialogComponent } from 'dialogs/timeapprove/timeapprove.dialogcomponent';
import { TimeRequestDialogComponent } from 'dialogs/timerequest/timerequest.dialogcomponent';
import { UserSearchDialogComponent } from 'dialogs/usersearch/usersearch.dialogcomponent';

// UI components
import { FormLabelComponent } from 'ui/formlabel/formlabel.component';
import { JCheckboxComponent } from 'ui/j-checkbox/jcheckbox.component';
import { NavMenuComponent } from 'ui/navmenu/navmenu.component';
import { UserInfoComponent } from 'ui/userinfo/userinfo.component';

// Service Components
import { SplashDialogComponent } from 'services/splash/splash.service';

// Directives
import { MobileTapDirective } from 'directives/MobileTap/mobiletap.directive';

// Pipes
import { BoardInfoPipe } from 'pipes/boardinfo.pipe';
import { CabinetTypePipe } from 'pipes/cabinettype.pipe';
import { CategoryPipe } from 'pipes/category.pipe';
import { CategoryCabinetPipe } from 'pipes/categorycabinet.pipe';
import { ClosedPipe } from 'pipes/closed.pipe';
import { DateFnsPipe } from 'pipes/datefns.pipe';
import { FileFilterPipe } from 'pipes/filefilter.pipe';
import { FileSizePipe } from 'pipes/filesize.pipe';
import { FileStatusPipe } from 'pipes/filestatus.pipe';
import { PhonePipe } from 'pipes/phone.pipe';
import { PriorityPipe } from 'pipes/priority.pipe';
import { PriorityTimePipe } from 'pipes/prioritytime.pipe';
import { ReferralFacilitatorStatusPipe } from 'pipes/referralfacilitatorstatus.pipe';
import { ReferralStatusPipe } from 'pipes/referralstatus.pipe';
import { RequestIssuePipe } from 'pipes/requestissue.pipe';
import { RequestStatusPipe } from 'pipes/requeststatus.pipe';
import { RoleTextPipe } from 'pipes/roletext.pipe';
import { SafeHTMLPipe } from 'pipes/safehtml.pipe';
import { SchoolNamePipe } from 'pipes/schoolname.pipe';
import { SchoolYearPipe } from 'pipes/schoolyear.pipe';
import { TextUnitPipe } from 'pipes/textunit.pipe';
import { TitleCasePipe } from 'pipes/titlecase.pipe';
import { YesNoPipe } from 'pipes/yesno.pipe';

import { ApplicationService } from 'services/application/application.service';

@NgModule({
    declarations: [
        // App components
        ActionNeededComponent,
        AppComponent,
        BoardAssistantsComponent,
        BoardRequestsComponent,
        CabinetMembersComponent,
        CabinetUnassignedComponent,
        CategoriesComponent,
        CreateComponent,
        DashboardComponent,
        EditRequestComponent,
        EmailPreferencesComponent,
        FacilitatorsComponent,
        HandleRequestComponent,
        HomeComponent,
        LoginComponent,
        OfflineComponent,
        OutOfOfficeComponent,
        OtherRequestsComponent,
        ReferralsComponent,
        RequestsComponent,
        SearchComponent,
        ViewRequestComponent,

        // UI components
        FormLabelComponent,
        JCheckboxComponent,
        NavMenuComponent,
        UserInfoComponent,

        // Service components
        SplashDialogComponent,

        // Dialog Components
        CheckboxDialogComponent,
        ConfirmationDialogComponent,
        CountdownDialogComponent,
        InputBoxDialogComponent,
        LoginBoxDialogComponent,
        MessageBoxDialogComponent,
        NotificationDialogComponent,
        OptionBoxDialogComponent,
        OutOfOfficeDesigneeSearchDialogComponent,
        ReportOptionsDialogComponent,
        SelectBoxDialogComponent,
        SelectBoxNoteDialogComponent,
        SubordinateDialogComponent,
        TextNoteDialogComponent,
        TimeApproveDialogComponent,
        TimeRequestDialogComponent,
        UserSearchDialogComponent,

        // Pipes
        BoardInfoPipe,
        CabinetTypePipe,
        CategoryCabinetPipe,
        CategoryPipe,
        ClosedPipe,
        DateFnsPipe,
        FileFilterPipe,
        FileSizePipe,
        FileStatusPipe,
        PhonePipe,
        PriorityPipe,
        PriorityTimePipe,
        ReferralFacilitatorStatusPipe,
        ReferralStatusPipe,
        RequestIssuePipe,
        RequestStatusPipe,
        RoleTextPipe,
        SafeHTMLPipe,
        SchoolNamePipe,
        SchoolYearPipe,
        TextUnitPipe,
        TitleCasePipe,
        YesNoPipe,

        // Directives
        MobileTapDirective,
    ],
    imports: [
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        HttpClientModule,
        AppMaterialModule,
        FlexLayoutModule,
        FileUploadModule,
        NgxWebstorageModule.forRoot(),
    ],
    providers: [
        AuthenticatedGuard,
        CabinetTypePipe,
        CategoryCabinetPipe,
        CategoryPipe,
        DatePipe,
        DateFnsPipe,
        DecimalPipe,
        FileSizePipe,
        PhonePipe,
        PriorityPipe,
        ReferralFacilitatorStatusPipe,
        ReferralStatusPipe,
        RequestIssuePipe,
        RequestStatusPipe,
        RoleTextPipe,
        SchoolYearPipe,
        TextUnitPipe,
        TitleCasePipe,
        UnsavedChangesGuard,
        UnsavedChangesService,
        YesNoPipe,
    ],
    entryComponents: [
        SplashDialogComponent,
        CheckboxDialogComponent,
        ConfirmationDialogComponent,
        CountdownDialogComponent,
        InputBoxDialogComponent,
        LoginBoxDialogComponent,
        MessageBoxDialogComponent,
        NotificationDialogComponent,
        OptionBoxDialogComponent,
        OutOfOfficeDesigneeSearchDialogComponent,
        ReportOptionsDialogComponent,
        SelectBoxDialogComponent,
        SelectBoxNoteDialogComponent,
        SubordinateDialogComponent,
        TextNoteDialogComponent,
        TimeApproveDialogComponent,
        TimeRequestDialogComponent,
        UserSearchDialogComponent,
    ],
    bootstrap: [AppComponent],
})
export class AppModule {
    constructor(private overlayContainer: OverlayContainer, private appSvc: ApplicationService) {
        appSvc.ThemeSubject.subscribe(value => {
            if (value) {
                overlayContainer.getContainerElement().classList.add('dark-iris');
            } else {
                overlayContainer.getContainerElement().classList.remove('dark-iris');
            }
        });
    }
}
